import { Pencil, Clock, ClipboardCheck, ListChecks } from 'lucide-react';
import type { TaskSummary } from '../../types';

/* Row-level markers shared by the ticket list and the Home widgets:
 * an unsent-draft mark (yours, and who else is drafting), the
 * "waiting on you" flag, and the sub-ticket task marks ("task for you",
 * progress). Text + color, never color alone. */

/**
 * You have an open sub-ticket task on this ticket — it's your work now.
 *
 * Deliberately tiny: an icon plus a count, never the task's own title. A
 * title inline pushed everything else off a narrow row, and the row already
 * carries the signal in its own title (see `taskForYouTitleStyle`). The
 * titles live in the tooltip, and the Tasks-for-me view lists them properly.
 */
export function TaskForYouMark({ tasks }: { tasks?: Array<{ id: string; title: string }> | null }) {
  const list = tasks ?? [];
  if (list.length === 0) return null;
  return (
    <span
      title={`Assigned to you on this ticket:\n${list.map(t => `• ${t.title}`).join('\n')}`}
      className="inline-flex items-center gap-1 text-[11px] font-bold shrink-0 whitespace-nowrap rounded-full px-1.5 py-0.5"
      style={{ color: 'var(--scpa-status-warning)', background: 'var(--scpa-primary-muted)' }}
    >
      <ClipboardCheck className="w-3 h-3 shrink-0" />
      {list.length === 1 ? 'Task' : `${list.length} tasks`}
    </span>
  );
}

/**
 * The ticket title itself carries "this one is on you": bold, in the same
 * amber the status chips and the waiting mark use. Colour is never the only
 * signal — the mark above sits beside it.
 */
export function taskForYouTitleStyle(tasks?: Array<unknown> | null): React.CSSProperties | undefined {
  return (tasks?.length ?? 0) > 0 ? { color: 'var(--scpa-status-warning)' } : undefined;
}

/** The task titles, listed under the row — for the Tasks-for-me view. */
export function TaskForYouList({ tasks }: { tasks?: Array<{ id: string; title: string }> | null }) {
  const list = tasks ?? [];
  if (list.length === 0) return null;
  return (
    <span className="block text-[11px] text-muted mt-0.5">
      {list.map(t => t.title).join(' · ')}
    </span>
  );
}

/** "2/5 tasks" — the parent's checklist progress, with who it waits on in the tooltip. */
export function TaskProgressChip({ summary }: { summary?: TaskSummary | null }) {
  if (!summary || summary.total === 0) return null;
  const allDone = summary.done === summary.total;
  const waiting = summary.waitingOn.map(w => `${w.name} (${w.count})`).join(', ');
  const title = allDone
    ? `All ${summary.total} tasks done`
    : [`${summary.done} of ${summary.total} tasks done`, waiting ? `waiting on ${waiting}` : null, summary.unassigned ? `${summary.unassigned} unassigned` : null]
        .filter(Boolean).join(' · ');
  return (
    <span
      title={title}
      className="inline-flex items-center gap-1 text-[11px] font-semibold shrink-0 whitespace-nowrap"
      style={{ color: allDone ? 'var(--scpa-status-ok)' : 'var(--scpa-text-muted)' }}
    >
      <ListChecks className="w-3 h-3" /> {summary.done}/{summary.total}
    </span>
  );
}

interface DraftAuthor {
  name: string;
  type: string;
}

const firstName = (name: string) => (name.trim().split(/\s+/)[0] || name).trim();

/** "✎ Draft" when you have one, "✎ Chris" when someone else does, both when both. */
export function DraftMark({ mine, others }: { mine?: boolean; others?: DraftAuthor[] | null }) {
  const people = others ?? [];
  if (!mine && people.length === 0) return null;

  const names = people.map(p => firstName(p.name));
  const kinds = new Set(people.map(p => (p.type === 'internal_note' ? 'an internal note' : 'a reply')));
  const what = kinds.size === 1 ? Array.from(kinds)[0] : 'a reply or note';
  const title = [
    mine ? 'You have an unsent draft on this ticket' : null,
    people.length > 0
      ? `${people.map(p => p.name).join(', ')} ${people.length === 1 ? 'is' : 'are'} drafting ${what}`
      : null,
  ].filter(Boolean).join('\n');

  const label = [mine ? 'Draft' : null, names.length > 0 ? names.join(', ') : null].filter(Boolean).join(' · ');

  return (
    <span
      title={title}
      className="inline-flex items-center gap-1 text-[11px] font-semibold shrink-0 whitespace-nowrap"
      style={{ color: 'var(--scpa-status-warning)' }}
    >
      <Pencil className="w-3 h-3" /> {label}
    </span>
  );
}

/** "Chris Sutton is also drafting a reply here" — for the compose box. */
export function describeDrafters(list: DraftAuthor[]): string {
  if (list.length === 0) return '';
  const names = list.map(p => p.name);
  const who = names.length <= 2
    ? names.join(' and ')
    : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
  const kinds = new Set(list.map(p => (p.type === 'internal_note' ? 'note' : 'reply')));
  let what: string;
  if (kinds.size > 1) what = '';
  else if (list.length === 1) what = ` ${kinds.has('note') ? 'an internal note' : 'a reply'}`;
  else what = ` ${kinds.has('note') ? 'internal notes' : 'replies'}`;
  return `${who} ${list.length === 1 ? 'is' : 'are'} also drafting${what} here`;
}

/** The ticket is in a waiting-on-customer status and you are who it waits on. */
export function WaitingOnYouMark({ compact = false }: { compact?: boolean }) {
  return (
    <span
      title="This ticket is waiting on you: a reply, a test, or a confirmation is needed before it can move on"
      className="inline-flex items-center gap-1 text-[11px] font-bold shrink-0 whitespace-nowrap rounded-full px-1.5 py-0.5"
      style={{ color: 'var(--scpa-status-warning)', background: 'var(--scpa-primary-muted)' }}
    >
      <Clock className="w-3 h-3" /> {compact ? 'On you' : 'Waiting on you'}
    </span>
  );
}
